import { Popover, PopoverContent, PopoverFooter, PopoverHeader } from '@/components/ui/Popover';
import { useState } from 'react';

// ============================================================================
// TIPOS
// ============================================================================

export type ExportFormat = 'csv' | 'excel' | 'pdf';
export type PdfOrientation = 'portrait' | 'landscape';

export interface ExportMenuProps {
  /** Callback para descargar el reporte */
  onExport: (format: ExportFormat) => void;
  /** Formato actualmente descargando (null si no hay descarga activa) */
  downloadingFormat: string | null;
  /** Si el botón está deshabilitado */
  disabled?: boolean;
  /** Orientación del PDF */
  pdfOrientation?: PdfOrientation;
  /** Callback cuando cambia la orientación */
  onOrientationChange?: (orientation: PdfOrientation) => void;
  /** Color del tema */
  themeColor?: 'blue' | 'red' | 'emerald';
  /** Si mostrar selector de orientación */
  showOrientationPicker?: boolean;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function ExportMenu({
  onExport,
  downloadingFormat,
  disabled = false,
  pdfOrientation = 'landscape',
  onOrientationChange,
  themeColor = 'blue',
  showOrientationPicker = true,
}: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Colores según tema
  const colorClasses = {
    blue: {
      primary: 'bg-blue-600 hover:bg-blue-700 text-white',
      outline: 'border-blue-600 text-blue-600 hover:bg-blue-50',
      radio: 'text-blue-600 focus:ring-blue-500',
    },
    red: {
      primary: 'bg-red-600 hover:bg-red-700 text-white',
      outline: 'border-red-600 text-red-600 hover:bg-red-50',
      radio: 'text-red-600 focus:ring-red-500',
    },
    emerald: {
      primary: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      outline: 'border-emerald-600 text-emerald-600 hover:bg-emerald-50',
      radio: 'text-emerald-600 focus:ring-emerald-500',
    },
  }[themeColor];

  const isDownloading = downloadingFormat !== null;

  const formatOptions: { format: ExportFormat; label: string; icon: JSX.Element }[] = [
    {
      format: 'csv',
      label: 'CSV',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      format: 'excel',
      label: 'Excel',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      format: 'pdf',
      label: 'PDF',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
    },
  ];

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={disabled}
      align="end"
      trigger={
        <button
          type="button"
          disabled={disabled || isDownloading}
          className={`
            inline-flex items-center gap-2 px-3 py-2
            bg-white border border-gray-300 rounded-lg
            text-sm font-medium text-gray-700
            hover:bg-gray-50 hover:border-gray-400
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          `}
        >
          {isDownloading ? (
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          )}
          <span className="hidden sm:inline">Exportar</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      }
    >
      <div className="w-64">
        {/* Header */}
        <PopoverHeader onClose={() => setIsOpen(false)}>
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Exportar Reporte
          </div>
        </PopoverHeader>

        {/* Orientación del PDF */}
        {showOrientationPicker && onOrientationChange && (
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <div className="text-xs font-medium text-gray-600 mb-2">Orientación PDF</div>
            <div className="flex gap-3">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="pdfOrientation"
                  checked={pdfOrientation === 'portrait'}
                  onChange={() => onOrientationChange('portrait')}
                  className={`w-3.5 h-3.5 ${colorClasses.radio}`}
                />
                <span className="ml-1.5 text-xs text-gray-700">Vertical</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="pdfOrientation"
                  checked={pdfOrientation === 'landscape'}
                  onChange={() => onOrientationChange('landscape')}
                  className={`w-3.5 h-3.5 ${colorClasses.radio}`}
                />
                <span className="ml-1.5 text-xs text-gray-700">Horizontal</span>
              </label>
            </div>
          </div>
        )}

        {/* Opciones de formato */}
        <PopoverContent className="p-3">
          <div className="grid grid-cols-3 gap-2">
            {formatOptions.map(({ format, label, icon }) => {
              const isCurrentDownloading = downloadingFormat === format;
              const buttonClass =
                format === 'csv'
                  ? `border ${colorClasses.outline}`
                  : format === 'excel'
                    ? colorClasses.primary
                    : `${colorClasses.primary} opacity-90`;

              return (
                <button
                  key={format}
                  type="button"
                  onClick={() => {
                    onExport(format);
                    // No cerrar el popover mientras descarga
                  }}
                  disabled={isDownloading}
                  className={`
                    flex flex-col items-center justify-center gap-1.5
                    p-3 rounded-lg
                    text-xs font-medium
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-all
                    ${buttonClass}
                  `}
                >
                  {isCurrentDownloading ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    icon
                  )}
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>

        {/* Footer con info */}
        <PopoverFooter>
          <p className="text-xs text-gray-500 text-center">
            Los archivos PDF están optimizados para A4
          </p>
        </PopoverFooter>
      </div>
    </Popover>
  );
}

// ============================================================================
// COMPONENTE SIMPLIFICADO (inline buttons)
// ============================================================================

export interface ExportButtonsProps {
  onExport: (format: ExportFormat) => void;
  downloadingFormat: string | null;
  disabled?: boolean;
  themeColor?: 'blue' | 'red' | 'emerald';
  size?: 'sm' | 'md';
}

export function ExportButtons({
  onExport,
  downloadingFormat,
  disabled = false,
  themeColor = 'blue',
  size = 'sm',
}: ExportButtonsProps) {
  const colorClasses = {
    blue: {
      csv: 'bg-green-600 hover:bg-green-700 text-white',
      excel: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      pdf: 'bg-red-600 hover:bg-red-700 text-white',
    },
    red: {
      csv: 'border border-red-600 text-red-600 hover:bg-red-50',
      excel: 'bg-red-600 hover:bg-red-700 text-white',
      pdf: 'bg-red-800 hover:bg-red-900 text-white',
    },
    emerald: {
      csv: 'border border-emerald-600 text-emerald-600 hover:bg-emerald-50',
      excel: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      pdf: 'bg-emerald-800 hover:bg-emerald-900 text-white',
    },
  }[themeColor];

  const sizeClasses = size === 'sm' ? 'px-2 py-2' : 'px-3 py-2.5';

  const isDownloading = downloadingFormat !== null;

  return (
    <div className="flex gap-2">
      {(['csv', 'excel', 'pdf'] as ExportFormat[]).map((format) => {
        const isCurrentDownloading = downloadingFormat === format;
        return (
          <button
            key={format}
            type="button"
            onClick={() => onExport(format)}
            disabled={disabled || isDownloading}
            className={`
              flex items-center justify-center gap-1.5
              ${sizeClasses} rounded-lg
              text-xs font-medium
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
              ${colorClasses[format]}
            `}
          >
            {isCurrentDownloading ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <span>{format.toUpperCase()}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default ExportMenu;
